import React, { FC, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  gray40,
  gray100,
  gray80,
  white,
  orange50,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../constants/colors';
import { boldMd, regularXl, regularLg } from '../../constants/location';

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  uploadCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  uploadCancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  uploadSubmitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  uploadSubmitButtonText: {
    ...boldMd,
    color: white,
  },
  radioGroupRow: {
    flexDirection: 'row',
  },
  radioOrange: {
    '&.Mui-checked': {
      color: orange50,
    },
  },
};

const FileExportDialog: FC<{
  open: boolean;
  onClose: () => void;
  onExport: (fileType: 'excel' | 'csv', encoding: string) => void;
}> = ({ open, onClose, onExport }) => {
  const [fileType, setFileType] = useState<'excel' | 'csv'>('csv');
  const [encoding, setEncoding] = useState<string>('utf-8');

  const handleExport = (): void => {
    onExport(fileType, encoding);
    onClose();
  };

  return (
    <Dialog open={open} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>ファイル出力</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <CloseIcon sx={styles.dialogCloseButton} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* 出力形式 */}
        <FormControl component="fieldset">
          <Typography sx={{ mb: 1, color: gray100 }}>出力形式</Typography>
          <RadioGroup
            row
            name="fileType"
            value={fileType}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
              setFileType(e.target.value as 'excel' | 'csv')
            }
            sx={styles.radioGroupRow}
          >
            <FormControlLabel
              value="excel"
              control={<Radio sx={styles.radioOrange} />}
              label="エクセル(.xlsx)"
            />
            <FormControlLabel value="csv" control={<Radio sx={styles.radioOrange} />} label="CSV" />
          </RadioGroup>
        </FormControl>

        {/* CSV文字コード（一括非表示かつレイアウト維持） */}
        <FormControl
          component="fieldset"
          sx={{
            opacity: fileType === 'csv' ? 1 : 0,
            pointerEvents: fileType === 'csv' ? 'auto' : 'none',
          }}
        >
          <Typography sx={{ mb: 1, color: gray100 }}>CSV文字コード</Typography>
          <RadioGroup
            row
            name="encoding"
            value={encoding}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setEncoding(e.target.value)}
            sx={styles.radioGroupRow}
          >
            <FormControlLabel
              value="utf-8"
              control={<Radio sx={styles.radioOrange} />}
              label="UTF-8(推奨)"
            />
            <FormControlLabel
              value="shift-jis"
              control={<Radio sx={styles.radioOrange} />}
              label="Shift-JIS"
            />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* ボタン */}
      <Box sx={styles.dialogButtonWrapper}>
        <Button sx={styles.uploadCancelButton} onClick={onClose}>
          <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
        </Button>
        <Button sx={styles.uploadSubmitButton} onClick={handleExport}>
          <Typography sx={styles.uploadSubmitButtonText}>出力する</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default FileExportDialog;
